/**
 * Fusion — core runtime
 *
 * Shared utilities and behaviours used across the theme. Loaded on every page.
 * Components are native custom elements and register themselves in their own files.
 *
 * Exposes a single global: window.Fusion
 */

(function () {
  'use strict';

  const root = document.documentElement;

  /* ======================================================================
     Configuration read from the DOM
     ====================================================================== */

  const config = {
    animations: root.dataset.animations === 'true',
    hoverAnimations: root.dataset.hoverAnimations === 'true',
    reduceMotion: root.dataset.reduceMotion === 'true',
    prefetch: root.dataset.prefetch === 'true',
    cartType: root.dataset.cartType || 'drawer',
    routes: window.Shopify?.routes || {},
  };

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Animations are suppressed when the merchant disables them OR when the
   * visitor has asked their OS for reduced motion and the merchant respects it.
   */
  function motionAllowed() {
    if (!config.animations) return false;
    if (config.reduceMotion && prefersReducedMotion.matches) return false;
    return true;
  }

  /* ======================================================================
     Utilities
     ====================================================================== */

  function debounce(fn, wait = 200) {
    let timer;
    return function debounced(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function throttle(fn, limit = 100) {
    let waiting = false;
    return function throttled(...args) {
      if (waiting) return;
      fn.apply(this, args);
      waiting = true;
      setTimeout(() => { waiting = false; }, limit);
    };
  }

  /** Formats a price in cents using the shop's active money format. */
  function formatMoney(cents) {
    const format = window.Fusion?.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, decimal] = value.split('.');
    const withSeparators = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return format
      .replace(/\{\{\s*amount\s*\}\}/, `${withSeparators}.${decimal}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withSeparators)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimal}`)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  /**
   * Fetches a rendered section from Shopify and returns it as a DOM tree.
   * Used by cart, filters, quick add and pagination to update without a reload.
   */
  async function fetchSection(sectionId, params = {}) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`Section ${sectionId} failed to load (${response.status})`);

    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /** Announces a message to screen readers via a shared live region. */
  function announce(message, assertive = false) {
    let region = document.getElementById('fusion-live-region');

    if (!region) {
      region = document.createElement('div');
      region.id = 'fusion-live-region';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      document.body.appendChild(region);
    }

    region.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
    // Clearing first guarantees the change is announced even for identical text
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 100);
  }

  /* ======================================================================
     Scroll locking
     ====================================================================== */

  let scrollLockCount = 0;

  function lockScroll() {
    scrollLockCount += 1;
    if (scrollLockCount > 1) return;

    const scrollbarWidth = window.innerWidth - root.clientWidth;
    root.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    document.body.classList.add('is-scroll-locked');
  }

  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount > 0) return;

    document.body.classList.remove('is-scroll-locked');
    root.style.removeProperty('--scrollbar-width');
  }

  /* ======================================================================
     Focus management
     ====================================================================== */

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'details > summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE))
      .filter((el) => el.offsetParent !== null || el === document.activeElement);
  }

  /**
   * Traps Tab focus inside a container. Returns a function that releases the
   * trap and restores focus to wherever it was before.
   */
  function trapFocus(container, initialFocus) {
    const previouslyFocused = document.activeElement;

    function handleKeydown(event) {
      if (event.key !== 'Tab') return;

      const focusable = getFocusable(container);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handleKeydown);

    const target = initialFocus || getFocusable(container)[0] || container;
    // Deferred so the element is painted and focusable before we call focus()
    window.requestAnimationFrame(() => target.focus());

    return function release() {
      container.removeEventListener('keydown', handleKeydown);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }

  /* ======================================================================
     Scroll reveal animations
     ====================================================================== */

  function initScrollAnimations() {
    const elements = document.querySelectorAll('[data-animate]:not(.is-visible)');
    if (elements.length === 0) return;

    // Without motion, reveal everything immediately rather than leaving it hidden
    if (!motionAllowed() || !('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, {
      // Reveals slightly before the element reaches the viewport edge
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.01,
    });

    elements.forEach((el) => observer.observe(el));
  }

  /* ======================================================================
     Chrome height tokens
     ====================================================================== */

  /**
   * Keeps --header-height and --announcement-height accurate so sticky offsets,
   * scroll padding and full-height heroes stay correct across breakpoints.
   */
  function syncChromeHeights() {
    const header = document.querySelector('[data-header]');
    const announcement = document.querySelector('[data-announcement]');

    root.style.setProperty('--header-height', `${header?.offsetHeight || 0}px`);
    root.style.setProperty('--announcement-height', `${announcement?.offsetHeight || 0}px`);
  }

  /* ======================================================================
     Link prefetching
     ====================================================================== */

  /**
   * Warms the browser cache for same-origin links on hover or touch start.
   * Each URL is fetched at most once per page view.
   */
  function initPrefetch() {
    if (!config.prefetch) return;
    if (navigator.connection?.saveData) return;

    const prefetched = new Set();

    function prefetch(event) {
      const link = event.target.closest('a');
      if (!link || !link.href) return;

      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname) return;
      if (link.hasAttribute('download') || link.dataset.noPrefetch !== undefined) return;
      if (prefetched.has(url.href)) return;

      prefetched.add(url.href);

      const hint = document.createElement('link');
      hint.rel = 'prefetch';
      hint.href = url.href;
      hint.as = 'document';
      document.head.appendChild(hint);
    }

    document.addEventListener('mouseover', prefetch, { passive: true, capture: true });
    document.addEventListener('touchstart', prefetch, { passive: true, capture: true });
  }

  /* ======================================================================
     Wishlist

     Handles live in localStorage only — no customer account or app
     required. Every wishlist button and the header count badge stay in
     sync purely through the "fusion:wishlist-change" document event; none
     of them reference each other directly.
     ====================================================================== */

  const WISHLIST_KEY = 'fusion:wishlist';

  function wishlistRead() {
    try {
      return JSON.parse(window.localStorage.getItem(WISHLIST_KEY)) || [];
    } catch (error) {
      return [];
    }
  }

  function wishlistWrite(handles) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
    } catch (error) {
      // Private browsing or a full quota — the toggle still updates on
      // screen for this page view, it just will not persist.
    }
    document.dispatchEvent(new CustomEvent('fusion:wishlist-change'));
  }

  const wishlist = {
    list: wishlistRead,
    count: () => wishlistRead().length,
    has: (handle) => wishlistRead().includes(handle),
    toggle(handle) {
      const current = wishlistRead();
      const has = current.includes(handle);
      wishlistWrite(has ? current.filter((h) => h !== handle) : [...current, handle]);
      return !has;
    },
    remove(handle) {
      wishlistWrite(wishlistRead().filter((h) => h !== handle));
    },
  };

  class FusionWishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.productHandle;
      this.button = this.querySelector('[data-wishlist-toggle]');
      this.button?.addEventListener('click', () => {
        const active = wishlist.toggle(this.handle);
        this.announce(active);
        if (active) this.playAddedAnimation();
      });

      this.sync = this.sync.bind(this);
      this.sync();
      document.addEventListener('fusion:wishlist-change', this.sync);
    }

    disconnectedCallback() {
      document.removeEventListener('fusion:wishlist-change', this.sync);
    }

    sync() {
      const active = wishlist.has(this.handle);
      this.button?.setAttribute('aria-pressed', String(active));
      this.classList.toggle('is-active', active);
    }

    announce(active) {
      const key = active ? 'wishlistAdded' : 'wishlistRemoved';
      announce(window.Fusion.strings?.[key] || '');
    }

    // Only plays when adding — removing shouldn't feel celebratory
    playAddedAnimation() {
      if (!this.button) return;
      this.button.classList.remove('is-popping');
      // Forces a reflow so the animation restarts on rapid re-toggling
      void this.button.offsetWidth;
      this.button.classList.add('is-popping');
    }
  }

  if (!customElements.get('fusion-wishlist-button')) {
    customElements.define('fusion-wishlist-button', FusionWishlistButton);
  }

  class FusionWishlistCount extends HTMLElement {
    connectedCallback() {
      this.sync = this.sync.bind(this);
      this.sync();
      document.addEventListener('fusion:wishlist-change', this.sync);
    }

    disconnectedCallback() {
      document.removeEventListener('fusion:wishlist-change', this.sync);
    }

    sync() {
      const count = wishlist.count();
      this.textContent = count;
      this.classList.toggle('is-empty', count === 0);
    }
  }

  if (!customElements.get('fusion-wishlist-count')) {
    customElements.define('fusion-wishlist-count', FusionWishlistCount);
  }

  /* ======================================================================
     Compare

     Same localStorage-only approach as Wishlist above, capped at 4 handles.
     The tray and modal fetch product data through the search endpoint
     (sections/compare-tray-items.liquid, sections/compare-table.liquid) —
     localStorage only ever holds handles, never images or prices.
     ====================================================================== */

  const COMPARE_KEY = 'fusion:compare';
  const COMPARE_MAX = 4;

  function compareRead() {
    try {
      return JSON.parse(window.localStorage.getItem(COMPARE_KEY)) || [];
    } catch (error) {
      return [];
    }
  }

  function compareWrite(handles) {
    try {
      window.localStorage.setItem(COMPARE_KEY, JSON.stringify(handles));
    } catch (error) {
      // Private browsing or a full quota — the toggle still updates on
      // screen for this page view, it just will not persist.
    }
    document.dispatchEvent(new CustomEvent('fusion:compare-change'));
  }

  const compare = {
    list: compareRead,
    count: () => compareRead().length,
    has: (handle) => compareRead().includes(handle),
    toggle(handle) {
      const current = compareRead();
      const has = current.includes(handle);
      if (!has && current.length >= COMPARE_MAX) return false;
      compareWrite(has ? current.filter((h) => h !== handle) : [...current, handle]);
      return !has;
    },
    remove(handle) {
      compareWrite(compareRead().filter((h) => h !== handle));
    },
    clear() {
      compareWrite([]);
    },
  };

  class FusionCompareCheckbox extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.productHandle;
      this.button = this.querySelector('[data-compare-toggle]');
      this.button?.addEventListener('click', () => {
        if (!compare.has(this.handle) && compare.count() >= COMPARE_MAX) {
          announce(window.Fusion.strings?.compareMax || '', true);
          return;
        }
        compare.toggle(this.handle);
      });

      this.sync = this.sync.bind(this);
      this.sync();
      document.addEventListener('fusion:compare-change', this.sync);
    }

    disconnectedCallback() {
      document.removeEventListener('fusion:compare-change', this.sync);
    }

    sync() {
      const active = compare.has(this.handle);
      this.button?.setAttribute('aria-pressed', String(active));
      this.classList.toggle('is-active', active);
    }
  }

  if (!customElements.get('fusion-compare-checkbox')) {
    customElements.define('fusion-compare-checkbox', FusionCompareCheckbox);
  }

  class FusionCompareTray extends HTMLElement {
    connectedCallback() {
      this.itemsEl = this.querySelector('[data-compare-tray-items]');

      this.querySelector('[data-compare-clear]')?.addEventListener('click', () => compare.clear());
      this.querySelector('[data-compare-open]')?.addEventListener('click', () => this.openModal());

      // The chips themselves are fetched HTML — one delegated listener
      // covers every remove button regardless of how many times it reloads.
      this.itemsEl?.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-compare-tray-remove]');
        if (removeButton) compare.remove(removeButton.dataset.productHandle);
      });

      this.sync = this.sync.bind(this);
      this.sync();
      document.addEventListener('fusion:compare-change', this.sync);
    }

    disconnectedCallback() {
      document.removeEventListener('fusion:compare-change', this.sync);
    }

    async sync() {
      const handles = compare.list();
      this.hidden = handles.length === 0;
      this.classList.toggle('is-visible', handles.length > 0);

      if (!this.itemsEl) return;

      if (handles.length === 0) {
        this.itemsEl.innerHTML = '';
        return;
      }

      const params = new URLSearchParams({
        q: handles.map((h) => `handle:${h}`).join(' OR '),
        type: 'product',
        'resources[limit]': handles.length,
        section_id: 'compare-tray-items',
      });

      try {
        const response = await fetch(`${window.Fusion.routes.search}?${params}`);
        if (!response.ok) throw new Error(`Compare tray failed (${response.status})`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        this.itemsEl.innerHTML = '';
        html.querySelectorAll('[data-compare-tray-item]').forEach((item) => {
          this.itemsEl.appendChild(item);
        });
      } catch (error) {
        console.error('[Fusion] Compare tray failed to load.', error);
      }
    }

    async openModal() {
      const modal = document.getElementById('CompareModal');
      const body = modal?.querySelector('[data-compare-body]');
      if (!modal || !body) return;

      modal.showModal();
      body.innerHTML = '<div class="spinner" aria-hidden="true"></div>';

      const handles = compare.list();
      const params = new URLSearchParams({
        q: handles.map((h) => `handle:${h}`).join(' OR '),
        type: 'product',
        'resources[limit]': handles.length,
        section_id: 'compare-table',
      });

      try {
        const response = await fetch(`${window.Fusion.routes.search}?${params}`);
        if (!response.ok) throw new Error(`Compare failed (${response.status})`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const table = html.querySelector('[data-compare-table]');
        body.innerHTML = table ? table.outerHTML : '';

        const toggle = modal.querySelector('[data-compare-highlight-toggle]');
        const tableEl = body.querySelector('[data-compare-table]');
        if (toggle && tableEl) {
          toggle.checked = false;
          tableEl.dataset.highlightDiff = 'false';
          toggle.addEventListener('change', () => {
            tableEl.dataset.highlightDiff = String(toggle.checked);
          });
        }
      } catch (error) {
        console.error('[Fusion] Compare failed to load.', error);
        body.innerHTML = '';
      }
    }
  }

  if (!customElements.get('fusion-compare-tray')) {
    customElements.define('fusion-compare-tray', FusionCompareTray);
  }

  /* ======================================================================
     Public API
     ====================================================================== */

  window.Fusion = Object.assign(window.Fusion || {}, {
    config,
    motionAllowed,
    debounce,
    throttle,
    formatMoney,
    fetchSection,
    announce,
    lockScroll,
    unlockScroll,
    trapFocus,
    getFocusable,
    syncChromeHeights,
    initScrollAnimations,
    wishlist,
    compare,
  });

  /* ======================================================================
     Generic <dialog> handler
     ====================================================================== */

  /**
   * Shared by every popup built on the component-dialog.css shell (size
   * guide, bulk enquiry, video lightbox, ...). Closing is a native
   * <form method="dialog"> submit (zero JS); this only handles opening and
   * click-outside-to-close. Lives here rather than in a feature-specific
   * file since this file loads exactly once, site-wide, on every page —
   * unlike per-section scripts, which can legitimately load more than once
   * on the same page and would need their own double-fire guard.
   */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-dialog-trigger]');
    if (trigger) {
      document.getElementById(trigger.dataset.dialogTarget)?.showModal();
      return;
    }

    const dialog = event.target.closest('[data-dialog]');
    // A click that lands on the ::backdrop bubbles with the dialog itself as
    // the target, never a descendant — that is what distinguishes it from a
    // click inside the panel.
    if (dialog && event.target === dialog) {
      dialog.close();
    }
  });

  /* ======================================================================
     Back to top
     ====================================================================== */

  function initBackToTop() {
    const button = document.querySelector('[data-back-to-top]');
    if (!button) return;

    const toggleVisibility = debounce(() => {
      button.classList.toggle('is-visible', window.scrollY > window.innerHeight);
    }, 100);

    window.addEventListener('scroll', toggleVisibility, { passive: true });
    toggleVisibility();

    button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
    });
  }

  /* ======================================================================
     Boot
     ====================================================================== */

  function init() {
    initScrollAnimations();
    initPrefetch();
    initBackToTop();
    syncChromeHeights();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('resize', debounce(syncChromeHeights, 150));

  // Re-run when the merchant edits a section in the theme editor
  document.addEventListener('shopify:section:load', () => {
    initScrollAnimations();
    syncChromeHeights();
  });

  document.addEventListener('shopify:section:unload', syncChromeHeights);
})();
